import { Component, EventEmitter, forwardRef, Input, Output } from '@angular/core';
import { IonInput } from '@ionic/angular/standalone';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';

@Component({
  selector: 'crowdctrl-input',
  standalone: true,
  imports: [IonInput],
  templateUrl: './input.component.html',
  styleUrls: ['../form-outline-field-tokens.css', './input.component.css'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => InputComponent),
      multi: true,
    },
  ],
})
export class InputComponent implements ControlValueAccessor {
  @Input() type: string = 'text';
  @Input() placeholder?: string;
  @Input() label?: string;
  @Input() labelPlacement: string = 'stacked';
  @Input() helperText?: string;
  @Input() required = false;
  @Input() inputmode?: string;
  @Input() minlength?: number;
  @Input() maxlength?: number;
  @Input() min?: number | string;
  @Input() max?: number | string;
  @Input() step?: number | string;
  @Input() pattern?: string;
  @Input() name?: string;
  @Input() debounce?: number;
  private _readonly = false;

  @Input()
  set readonly(value: boolean | string | null | undefined) {
    this._readonly = value === '' || value === true || value === 'true';
  }
  get readonly(): boolean {
    return this._readonly;
  }

  @Input('readOnly')
  set readOnly(value: boolean | string | null | undefined) {
    this.readonly = value;
  }
  @Input() disabled = false;
  @Input() clearInput = true;
  @Input() clearOnEdit?: boolean;
  @Input() autocapitalize: string = 'sentences';
  @Input() autocorrect?: 'on' | 'off' | string;
  @Input() autocomplete?: string;
  @Input() enterkeyhint?: string;
  @Input() inputId?: string;

  @Output() ionInput = new EventEmitter<Event>();
  @Output() ionChange = new EventEmitter<Event>();
  @Output() ionBlur = new EventEmitter<Event>();
  @Output() ionFocus = new EventEmitter<Event>();
  @Output() ionClear = new EventEmitter<Event>();

  @Input() value: string | number | null = '';

  private onChange: (value: string | number | null) => void = () => {};
  private onTouched: () => void = () => {};

  writeValue(value: string | number | null): void {
    this.value = value ?? '';
  }

  registerOnChange(fn: (value: string | number | null) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  handleInput(event: Event): void {
    const detailValue = (event as CustomEvent).detail as { value?: string | number } | undefined;
    const value = detailValue?.value ?? '';
    this.value = value;
    this.onChange(value);
    this.ionInput.emit(event);
  }

  handleBlur(event: Event): void {
    this.onTouched();
    this.ionBlur.emit(event);
  }
}
